import React, { useState, useEffect } from 'react';
import { scheduleApi } from '../../lib/api';
import Button from '../common/Button';
import Input from '../common/Input';
import Loader from '../common/Loader';

interface Schedule {
    id?: string;
    name: string;
    startTime: string;
    endTime: string;
    color?: string;
    // Periodo de gracia
    graceEnabled?: boolean;
    graceInBeforeStartMin?: number;
    graceInNoLateAfterStartMin?: number;
    graceInAfterStartMaxMin?: number;
    graceOutBeforeEndMin?: number;
    graceOutAfterEndMin?: number;
}

// Para compatibilidad con datos antiguos que puedan venir del backend
// interface LegacySchedule {
//     id?: string;
//     name: string;
//     dayOfWeek?: number;
//     startTime: string;
//     endTime: string;
// }

interface ScheduleFormProps {
    schedule?: Schedule;
    onSuccess: () => void;
    onCancel: () => void;
}

const ScheduleForm: React.FC<ScheduleFormProps> = ({ schedule, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState<Schedule>({
        name: '',
        startTime: '09:00',
        endTime: '17:00',
        color: '#3B82F6',
        // Periodo de gracia (valores por defecto)
        graceEnabled: false,
        graceInBeforeStartMin: 0,
        graceInNoLateAfterStartMin: 0,
        graceInAfterStartMaxMin: 0,
        graceOutBeforeEndMin: 0,
        graceOutAfterEndMin: 0,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (schedule) {
            setFormData({
                name: schedule.name,
                startTime: schedule.startTime,
                endTime: schedule.endTime,
                color: schedule.color || '#3B82F6',
                // Periodo de gracia
                graceEnabled: schedule.graceEnabled || false,
                graceInBeforeStartMin: schedule.graceInBeforeStartMin || 0,
                graceInNoLateAfterStartMin: schedule.graceInNoLateAfterStartMin || 0,
                graceInAfterStartMaxMin: schedule.graceInAfterStartMaxMin || 0,
                graceOutBeforeEndMin: schedule.graceOutBeforeEndMin || 0,
                graceOutAfterEndMin: schedule.graceOutAfterEndMin || 0,
            });
        }
    }, [schedule]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        // Verificar si es un checkbox para obtener el valor correcto
        let inputValue: any;

        if (type === 'checkbox' && 'checked' in e.target) {
            // Para checkboxes, usar el valor booleano
            inputValue = (e.target as HTMLInputElement).checked;
        } else if (type === 'number') {
            // Para inputs numéricos, convertir el valor a number
            inputValue = value === '' ? 0 : parseInt(value, 10);
        } else {
            // Para otros inputs, usar el valor como string
            inputValue = value;
        }

        setFormData(prev => ({
            ...prev,
            [name]: inputValue,
        }));
    };


    // Función para formatear ventana de tiempo
    const formatTimeWindow = (time: string, beforeMin: number, afterMin: number): string => {
        const [hours, minutes] = time.split(':').map(Number);
        const timeDate = new Date();
        timeDate.setHours(hours, minutes, 0, 0);

        const startTime = new Date(timeDate.getTime() - beforeMin * 60 * 1000);
        const endTime = new Date(timeDate.getTime() + afterMin * 60 * 1000);

        return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    };

    // Función para formatear tiempo con gracia
    const formatTimeWithGrace = (time: string, graceMin: number): string => {
        const [hours, minutes] = time.split(':').map(Number);
        const timeDate = new Date();
        timeDate.setHours(hours, minutes, 0, 0);

        const graceTime = new Date(timeDate.getTime() + graceMin * 60 * 1000);

        return formatTime(graceTime);
    };

    // Función para formatear hora
    const formatTime = (date: Date): string => {
        return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (schedule?.id) {
                await scheduleApi.updateSchedule(schedule.id, formData);
            } else {
                await scheduleApi.createSchedule(formData);
            }
            onSuccess();
        } catch (error: any) {
            setError(error.message || 'Error al guardar el horario');
        } finally {
            setLoading(false);
        }
    };


    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            <div className="space-y-6">
                <Input
                    label="Nombre del turno *"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Turno mañana, Turno tarde, etc."
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                        label="Hora de inicio *"
                        name="startTime"
                        type="time"
                        value={formData.startTime}
                        onChange={handleInputChange}
                        required
                    />

                    <Input
                        label="Hora de fin *"
                        name="endTime"
                        type="time"
                        value={formData.endTime}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Color del turno
                    </label>
                    <div className="flex items-center space-x-3">
                        <input
                            type="color"
                            name="color"
                            value={formData.color}
                            onChange={handleInputChange}
                            className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                            type="text"
                            name="color"
                            value={formData.color}
                            onChange={handleInputChange}
                            placeholder="#3B82F6"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            pattern="^#[0-9A-Fa-f]{6}$"
                            title="Color en formato HEX (#RRGGBB)"
                        />
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                        El color se usará para identificar visualmente este turno en el calendario
                    </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700">
                        <strong>Nota:</strong> Los turnos creados aquí estarán disponibles en el calendario semanal
                        para asignarlos a empleados en días específicos.
                    </p>
                </div>

                {/* Periodo de gracia */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Periodo de Gracia (Tolerancias)
                    </h3>

                    <div className="flex items-center space-x-3 mb-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                name="graceEnabled"
                                checked={formData.graceEnabled || false}
                                onChange={handleInputChange}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Habilitar periodo de gracia
                            </span>
                        </label>
                        <p className="text-sm text-gray-500">
                            Permite tolerancias alrededor del inicio y fin del turno
                        </p>
                    </div>

                    {formData.graceEnabled && (
                        <div className="space-y-6">
                            {/* Ventana de entrada */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-blue-900 mb-3">
                                    Ventana de Entrada
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Antes del inicio (min)
                                        </label>
                                        <Input
                                            label=""
                                            name="graceInBeforeStartMin"
                                            type="number"
                                            min="0"
                                            value={formData.graceInBeforeStartMin || 0}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            No retraso hasta (min)
                                        </label>
                                        <Input
                                            label=""
                                            name="graceInNoLateAfterStartMin"
                                            type="number"
                                            min="0"
                                            value={formData.graceInNoLateAfterStartMin || 0}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Máximo después (min)
                                        </label>
                                        <Input
                                            label=""
                                            name="graceInAfterStartMaxMin"
                                            type="number"
                                            min="0"
                                            value={formData.graceInAfterStartMaxMin || 0}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                                <p className="mt-2 text-xs text-gray-600">
                                    <strong>Entrada válida:</strong> {formatTimeWindow(formData.startTime, formData.graceInBeforeStartMin || 0, formData.graceInAfterStartMaxMin || 0)}
                                </p>
                            </div>

                            {/* Ventana de salida */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-green-900 mb-3">
                                    Ventana de Salida
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Antes del fin (min)
                                        </label>
                                        <Input
                                            label=""
                                            name="graceOutBeforeEndMin"
                                            type="number"
                                            min="0"
                                            value={formData.graceOutBeforeEndMin || 0}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Después del fin (min)
                                        </label>
                                        <Input
                                            label=""
                                            name="graceOutAfterEndMin"
                                            type="number"
                                            min="0"
                                            value={formData.graceOutAfterEndMin || 0}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                                <p className="mt-2 text-xs text-gray-600">
                                    <strong>Salida válida:</strong> {formatTimeWindow(formData.endTime, formData.graceOutBeforeEndMin || 0, formData.graceOutAfterEndMin || 0)}
                                </p>
                            </div>

                            {/* Previsualización de ventanas */}
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-yellow-900 mb-3">
                                    Previsualización de Ventanas
                                </h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-700">Inicio del turno:</span>
                                        <span className="font-medium text-gray-900">{formData.startTime}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-700">Fin del turno:</span>
                                        <span className="font-medium text-gray-900">{formData.endTime}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-700">Entrada válida:</span>
                                        <span className="font-medium text-blue-700">{formatTimeWindow(formData.startTime, formData.graceInBeforeStartMin || 0, formData.graceInAfterStartMaxMin || 0)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-700">No retraso hasta:</span>
                                        <span className="font-medium text-green-700">{formatTimeWithGrace(formData.startTime, formData.graceInNoLateAfterStartMin || 0)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-700">Salida válida:</span>
                                        <span className="font-medium text-green-700">{formatTimeWindow(formData.endTime, formData.graceOutBeforeEndMin || 0, formData.graceOutAfterEndMin || 0)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <Button
                    type="button"
                    onClick={onCancel}
                    variant="secondary"
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    disabled={loading}
                >
                    {loading && <Loader size="sm" />}
                    {schedule?.id ? 'Actualizar Horario' : 'Crear Horario'}
                </Button>
            </div>
        </form>
    );
};

export default ScheduleForm;